import { useCallback, useMemo } from 'react';
import getSearchServiceImpl from '../services/search/searchServiceDiscovery';
import { SEARCH_SERVICE_TYPE, PERMISSION_TYPE, LEGO_CONSTANTS } from 'constants/index';
import { CommonUtils, getEntityRouteParam } from 'utils';
import { shouldUseTypeBasedEntityApi } from 'utils/entityIdentifier';
import { useFreshdeskContextState, useNativeObjectsState } from 'store';
import useMessageChannelAdapter from './use-message-channel-adapter';
import nativeObjectConfigs from 'configs/nativeObjectConfigs';

const useSearch = () => {
  const nativeObjectData = useNativeObjectsState();
  const { getRecordPermissions, getConfigType } = useFreshdeskContextState();

  const configType = getConfigType();
  const { search: searchMC, searchNativeLookupChoices } = useMessageChannelAdapter();
  const recordPermissions = useMemo(() => getRecordPermissions(), [getRecordPermissions]);

  const constructErrorObject = useCallback(message => {
    return { data: { message } };
  }, []);

  const isNative = useCallback(
    entityId => {
      return Object.keys(nativeObjectData.idLookup).includes(entityId?.toString());
    },
    [nativeObjectData]
  );

  const searchRecords = useCallback(
    async (term, entityId, searchField, searchServiceType, params = {}) => {
      try {
        if (!recordPermissions?.isPresent(PERMISSION_TYPE.READ)) throw constructErrorObject(LEGO_CONSTANTS.NO_RECORD_READ_PERMISSION);
        if (searchServiceType === SEARCH_SERVICE_TYPE.NATIVE_LOOKUP) {
          return await searchNativeLookupChoices(entityId, { query: term, page: 1, per_page: LEGO_CONSTANTS.SEARCH_PAGE_SIZE });
        } else if (isNative(entityId)) {
          const config = nativeObjectConfigs[configType][searchServiceType];
          const nativeParams = { options: config.searchParams, ...params };
          return await searchMC(searchServiceType, { term, schema_id: entityId, page_size: LEGO_CONSTANTS.SEARCH_PAGE_SIZE, subParameters: nativeParams.options }, configType);
        } else {
          const service = getSearchServiceImpl(searchServiceType);
          const response = await service.search(
            {},
            { term: encodeURIComponent(term), schema_id: entityId, search_field: searchField, page_size: LEGO_CONSTANTS.SEARCH_PAGE_SIZE, context: searchServiceType, ...params }
          );
          return CommonUtils.buildResponse(true, response);
        }
      } catch (error) {
        console.error(error);
        return CommonUtils.buildErrorResponse(error);
      }
    },
    [recordPermissions, constructErrorObject, isNative, configType, searchMC, searchNativeLookupChoices]
  );

  const processSearchResponse = useCallback(
    (response, type, fieldName, searchServiceType) => {
      try {
        switch (searchServiceType) {
          case SEARCH_SERVICE_TYPE.NATIVE_LOOKUP: {
            return response?.choices?.map(choice => ({
              text: choice.value,
              value: choice.id,
              context: SEARCH_SERVICE_TYPE.NATIVE_LOOKUP,
              meta: choice,
            }));
          }
          case SEARCH_SERVICE_TYPE.ENTITY_RECORD: {
            const searchResponse = response?.records?.map(record => {
              const { display_id: displayId, data, _links: recordLinks } = record;
              const recordResponse = {
                text: data[fieldName],
                value: type === LEGO_CONSTANTS.LOOKUP ? displayId : data[fieldName],
                context: SEARCH_SERVICE_TYPE.ENTITY_RECORD,
              };
              if (type === LEGO_CONSTANTS.LOOKUP) {
                recordResponse.meta = record;
              }
              // Preserve record-level links if present (e.g., associations links)
              if (recordLinks) {
                recordResponse._links = recordLinks;
              }
              return recordResponse;
            });
            const result = type === LEGO_CONSTANTS.LOOKUP ? searchResponse : CommonUtils.getUniqueItemsByProperties(searchResponse, ['value']);
            // Preserve pagination links from response
            if (response?._links) {
              result._links = response._links;
            }
            return result;
          }
          default: {
            const config = nativeObjectConfigs[configType][searchServiceType];
            return response?.[config.searchResponseKey]?.map(item => {
              if (config.variation === LEGO_CONSTANTS.CONVERSATION) {
                return {
                  text: item[config.defaultDisplayField],
                  subText: item.rawTitle,
                  graphicsProps: { name: 'freshchat' },
                  metaText: {
                    name: item?.firstName + ' ' + item?.lastName,
                    email: item?.email,
                    phone: item?.phone,
                  },
                  value: item[config.ucrEnabledIdKey],
                  context: searchServiceType,
                  meta: item,
                };
              }
              return {
                text: item[config.defaultDisplayField],
                value: item[config.ucrEnabledIdKey],
                context: searchServiceType,
                meta: item,
              };
            });
          }
        }
      } catch (e) {
        return [];
      }
    },
    [configType]
  );

  const executeForSearchType = useCallback(
    async (term, relatedEntityId, fieldName, searchServiceType, params = {}, type = LEGO_CONSTANTS.LOOKUP, processResponse = true) => {
      const response = await searchRecords(term, relatedEntityId, fieldName, searchServiceType, params);
      if (!response.success) return [];
      return processResponse ? processSearchResponse(response.response, type, fieldName, searchServiceType) : response.response;
    },
    [processSearchResponse, searchRecords]
  );

  const __getSearchServiceTypeForNativeSchema = useCallback(
    id => {
      let service = null;
      if (nativeObjectData?.idLookup[id]) {
        service = nativeObjectData.idLookup[id]?.context;
      }
      return service;
    },
    [nativeObjectData]
  );

  const getSearchServiceType = useCallback(
    (relatedEntityId, fieldOptions) => {
      // typeBasedApi hosts (e.g. itildesk) don't have Freshdesk-style native ticket/contact/company objects -
      // under that flag, a NATIVE relationship can only be a datasource native_lookup target.
      if (shouldUseTypeBasedEntityApi() && fieldOptions?.related_object_type === 'NATIVE') {
        return SEARCH_SERVICE_TYPE.NATIVE_LOOKUP;
      }
      return isNative(relatedEntityId) ? __getSearchServiceTypeForNativeSchema(relatedEntityId, nativeObjectData) : SEARCH_SERVICE_TYPE.ENTITY_RECORD;
    },
    [__getSearchServiceTypeForNativeSchema, isNative, nativeObjectData]
  );

  const executeSearch = useCallback(
    async (context, params = {}) => {
      try {
        let { type, term, relatedEntityId, fieldName, entity, associatedEntities, fieldOptions } = context;
        let toSearchFor = getEntityRouteParam(entity);
        let searchServiceType = SEARCH_SERVICE_TYPE.ENTITY_RECORD;
        if (type === LEGO_CONSTANTS.LOOKUP) {
          searchServiceType = getSearchServiceType(relatedEntityId, fieldOptions);
          const relatedEntity = associatedEntities.filter(e => e.id === relatedEntityId)?.[0];
          fieldName = CommonUtils.getPrimaryField(relatedEntity)?.name;
          // Native schemas still key off the raw id; only entity-record lookups need the route/type param.
          toSearchFor = searchServiceType === SEARCH_SERVICE_TYPE.ENTITY_RECORD ? getEntityRouteParam(relatedEntity) ?? relatedEntityId : relatedEntityId;
        }
        return executeForSearchType(term, toSearchFor, fieldName, searchServiceType, params, type);
      } catch (e) {
        return [];
      }
    },
    [executeForSearchType, getSearchServiceType]
  );

  return useMemo(() => {
    return {
      executeSearch,
      executeForSearchType,
      getSearchServiceType,
    };
  }, [executeSearch, executeForSearchType, getSearchServiceType]);
};

export default useSearch;
